import { joinCompiledChapters, WHOLE_BOOK_SPAN_ID, type TimelineSpan } from "./join-chapters";
import type { CompiledChapter } from "./schema/compiled";

const RANGE_SEPARATOR = "..";

export interface ChapterRange {
  first: number;
  last: number;
}

/** A span id is `book`, one chapter (`ch02`), or a run of them (`ch02..ch03`). */
export function chapterSpans(spans: readonly TimelineSpan[]): TimelineSpan[] {
  return spans.filter((span) => span.id !== WHOLE_BOOK_SPAN_ID);
}

export function chapterRangeFor(
  spans: readonly TimelineSpan[],
  spanId: string,
): ChapterRange | null {
  const chapters = chapterSpans(spans);
  if (spanId === WHOLE_BOOK_SPAN_ID) {
    return chapters.length === 0 ? null : { first: 0, last: chapters.length - 1 };
  }
  const [from, to = from] = spanId.split(RANGE_SEPARATOR);
  const first = chapters.findIndex((chapter) => chapter.id === from);
  const last = chapters.findIndex((chapter) => chapter.id === to);
  return first === -1 || last === -1 || first > last ? null : { first, last };
}

export function spanIdForRange(
  spans: readonly TimelineSpan[],
  { first, last }: ChapterRange,
): string {
  const chapters = chapterSpans(spans);
  if (first === 0 && last === chapters.length - 1) {
    return WHOLE_BOOK_SPAN_ID;
  }
  const from = chapters[first]?.id ?? WHOLE_BOOK_SPAN_ID;
  const to = chapters[last]?.id ?? from;
  return first === last ? from : `${from}${RANGE_SEPARATOR}${to}`;
}

/**
 * Anything outside joins, with those between. A playing chapter comes off along with
 * everything on its shorter side (the earlier side on a tie), so no gap can form.
 */
export function toggleChapter(
  { first, last }: ChapterRange,
  index: number,
): ChapterRange {
  if (index < first) {
    return { first: index, last };
  }
  if (index > last) {
    return { first, last: index };
  }
  if (first === last) {
    return { first, last };
  }
  return index - first <= last - index
    ? { first: index + 1, last }
    : { first, last: index - 1 };
}

/**
 * A drag across chapters. Starting on a lit chapter turns off everything dragged over, from
 * whichever side leaves no gap. Starting on an unlit one lights everything up to the end.
 * At least one chapter always stays on.
 */
export function dragChapters(range: ChapterRange, start: number, end: number): ChapterRange {
  if (start === end) {
    return toggleChapter(range, start);
  }
  const { first, last } = range;
  const low = Math.min(start, end);
  const high = Math.max(start, end);
  if (start < first || start > last) {
    return { first: Math.min(first, low), last: Math.max(last, high) };
  }
  const next = low - first <= last - high
    ? { first: high + 1, last }
    : { first, last: low - 1 };
  return next.first > next.last ? { first: end, last: end } : next;
}

/** "chapter 2" or "chapters 2–4", from zero-based positions in the book. */
export function describeChapters({ first, last }: ChapterRange): string {
  return first === last ? `chapter ${first + 1}` : `chapters ${first + 1}–${last + 1}`;
}

/** Only these chapters exist on the rail, so nothing from the others can show. */
export function joinSelectedChapters(
  chapters: readonly CompiledChapter[],
  spans: readonly TimelineSpan[],
  spanId: string,
): { chapter: CompiledChapter; spans: TimelineSpan[] } {
  const range = chapterRangeFor(spans, spanId);
  const ids = new Set(
    chapterSpans(spans)
      .slice(range?.first ?? 0, (range?.last ?? spans.length) + 1)
      .map((span) => span.id),
  );
  const joined = joinCompiledChapters(chapters.filter((item) => ids.has(item.chapterId)));
  // Milliseconds mean something different per selection, so each saves its own place.
  return { ...joined, chapter: { ...joined.chapter, chapterId: spanId } };
}
